import React, { useState } from 'react'
import { Form, Input, Select, Button, App, Typography, Space } from 'antd'
import { supabaseClient } from '../../lib/supabaseClient'
import { ListPage, SectionCard } from '../../theme/listkit'

type Audience = 'all' | 'class_year' | 'admins'

/**
 * What a tap on the push opens. '' sends no screen, so the app opens its
 * Notifications list, as every broadcast did before this option existed.
 * The others are no-param screens the app allowlists; send-push refuses any
 * screen outside ROUTE_NO_PARAM (_shared/communityProposal.ts), and
 * sendPush.test.tsx fails if this list offers one. Screens that only make
 * sense for one member (MyBusiness) are left out of a broadcast on purpose.
 */
export const OPEN_SCREEN_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Notifications (default)' },
  { value: 'SuggestionBox', label: 'Suggestion Box' },
  { value: 'Gallery', label: 'Photo Gallery' },
  { value: 'Give', label: 'Give' },
  { value: 'Mentorship', label: 'Mentorship' },
]

/**
 * Shape the `send-push` Edge Function returns on a 2xx. Its non-2xx bodies
 * (`{ error }` for a missing/invalid payload, a non-admin caller, or a failed
 * token query) never reach `data` — supabase-js converts any non-2xx into a
 * FunctionsHttpError on `error`, which the catch below reports. `ok` is false —
 * with `reason` and a human-readable `error` — when the audience matched zero
 * registered devices, which is a failure, not a send.
 */
export interface SendPushResult {
  ok: boolean
  sent: number
  tickets: unknown[]
  reason?: 'no_recipients' | 'flag_off' | 'route_feature_off'
  error?: string
}

/**
 * Send Push — broadcasts an Expo push notification to members with a registered
 * device. Calls the admin-gated `send-push` Edge Function (which holds no secret
 * the browser shouldn't; it fans out to the Expo Push API server-side). Controlled
 * state keeps the action deterministic and testable.
 */
export function SendPushPage() {
  const { message } = App.useApp()
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [audience, setAudience] = useState<Audience>('all')
  const [classYear, setClassYear] = useState('')
  const [openScreen, setOpenScreen] = useState('')
  const [sending, setSending] = useState(false)

  const canSend =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    !sending &&
    (audience !== 'class_year' || classYear.trim().length > 0)

  const onSend = async () => {
    if (!canSend) return
    setSending(true)
    try {
      const { data, error } = await supabaseClient.functions.invoke<SendPushResult>('send-push', {
        body: {
          title: title.trim(),
          body: body.trim(),
          audience,
          ...(audience === 'class_year' ? { classYear: classYear.trim() } : {}),
          ...(openScreen ? { screen: openScreen } : {}),
        },
      })
      if (error) throw error

      // Reaching zero devices is NOT success. The function used to return
      // ok:true at 0 recipients and this line cheerfully reported "Push sent to
      // 0 device(s)" — which is how five broadcasts in one day looked like they
      // worked while nobody had a registered device. Say what actually happened
      // and leave the composed message in the form so it isn't retyped.
      const sent = Number(data?.sent ?? 0)
      if (sent === 0) {
        message.warning(
          data?.error ??
            'Nothing was sent — no registered devices matched this audience. Members appear here only after opening the app on a physical device and allowing notifications.',
          8,
        )
        return
      }

      message.success(`Push sent to ${sent} device${sent === 1 ? '' : 's'}`)
      setTitle('')
      setBody('')
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      message.error(`Failed to send: ${msg}`)
    } finally {
      setSending(false)
    }
  }

  return (
    <ListPage
      intro={{
        eyebrow: 'Messaging · Push',
        description:
          'Broadcast an Expo push notification to members with a registered device. Target everyone, a single class year, or only admins — delivery is best-effort and runs server-side.',
      }}
    >
      <SectionCard title="Send Push Notification" style={{ maxWidth: 640 }}>
        <Form layout="vertical">
          <Form.Item label="Title" required>
            <Input
              placeholder="Notification title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
            />
          </Form.Item>
          <Form.Item label="Message" required>
            <Input.TextArea
              rows={3}
              placeholder="Message body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={240}
            />
          </Form.Item>
          <Form.Item label="Audience">
            <Select
              aria-label="Audience"
              value={audience}
              onChange={(v: Audience) => setAudience(v)}
              options={[
                { value: 'all', label: 'All members' },
                { value: 'class_year', label: 'By class year' },
                { value: 'admins', label: 'Admins only' },
              ]}
            />
          </Form.Item>
          {audience === 'class_year' && (
            <Form.Item label="Class year">
              <Input
                placeholder="e.g. 2001"
                value={classYear}
                onChange={(e) => setClassYear(e.target.value)}
              />
            </Form.Item>
          )}
          <Form.Item
            label="When tapped, open"
            extra="The screen a member lands on. Members on an app version that doesn't have it yet land on Notifications."
          >
            <Select
              aria-label="When tapped, open"
              value={openScreen}
              onChange={(v: string) => setOpenScreen(v)}
              options={OPEN_SCREEN_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            />
          </Form.Item>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button type="primary" onClick={onSend} disabled={!canSend} loading={sending}>
              Send Notification
            </Button>
            <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
              Delivers an Expo push to members with a registered device. Best-effort delivery;
              unregistered tokens are skipped server-side.
            </Typography.Paragraph>
          </Space>
        </Form>
      </SectionCard>
    </ListPage>
  )
}
